import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import axios from 'axios';
import { API } from '../config/api';

const categoryImages = {
  'Smash Burger': 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=100&q=80',
  'Hamburger': 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=100&q=80',
  'Grill/Kebab': 'https://images.unsplash.com/photo-1529006557810-274b9b2fc783?w=100&q=80',
  'Sides': 'https://images.unsplash.com/photo-1630384060421-cb20adf7ed21?w=100&q=80',
  'Italiensk Pizza': 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=100&q=80',
  'Biff/Kylling Pizza': 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=100&q=80',
  'Mexicansk Pizza': 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=100&q=80',
  'Kebab Pizza': 'https://images.unsplash.com/photo-1588315029754-2dd089d39a1a?w=100&q=80',
  'Ost i kanten Pizza': 'https://images.unsplash.com/photo-1595708684082-a173bb3a06c5?w=100&q=80',
  'Spesialitet': 'https://images.unsplash.com/photo-1590947132387-155cc02f3212?w=100&q=80',
  'Sauser': 'https://images.unsplash.com/photo-1472476443507-c7a5948772fc?w=100&q=80',
  'Barnemeny': 'https://images.unsplash.com/photo-1619881589928-d9a0b1c6e7f4?w=100&q=80',
  'Drikke': 'https://images.unsplash.com/photo-1543253687-c931c8e01820?w=100&q=80',
};

export const MenuPage = () => {
  const { t, language } = useLanguage();
  const [menu, setMenu] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API}/menu`);
        setMenu(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching menu:', error);
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [menu]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -250 : 250,
        behavior: 'smooth'
      });
      setTimeout(checkScroll, 300);
    }
  };

  const getCategoryName = (category) => language === 'no' ? category.name : category.name_en;

  const filteredMenu = selectedCategory 
    ? menu.filter(cat => cat.name === selectedCategory)
    : menu;

  const searchFilteredMenu = searchQuery 
    ? filteredMenu.map(cat => ({
        ...cat,
        items: cat.items.filter(item => 
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.items.length > 0)
    : filteredMenu;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-800 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'no' ? 'Laster meny...' : 'Loading menu...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="menu-page" className="min-h-screen bg-[#FAFAF8] pt-24 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 data-testid="menu-title" className="font-heading text-4xl md:text-5xl text-gray-900 mb-3">
            {t('menuTitle')}
          </h1>
          <p className="text-gray-500">{t('menuSubtitle')}</p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'no' ? 'Søk i menyen...' : 'Search menu...'}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
              data-testid="menu-search"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-10 relative">
          {canScrollLeft && (
            <button onClick={() => scroll('left')} data-testid="scroll-left-btn"
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50">
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
          )}
          
          {canScrollRight && (
            <button onClick={() => scroll('right')} data-testid="scroll-right-btn"
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50">
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          )}

          <div ref={scrollRef} onScroll={checkScroll} className="flex gap-3 overflow-x-auto px-8 scrollbar-hide">
            <button onClick={() => setSelectedCategory(null)} data-testid="category-filter-all"
              className={`category-tab ${selectedCategory === null ? 'active' : ''}`}>
              {t('allCategories')}
            </button>
            {menu.map((category, index) => (
              <button key={index} onClick={() => setSelectedCategory(category.name)}
                data-testid={`category-filter-${index}`}
                className={`category-tab ${selectedCategory === category.name ? 'active' : ''}`}>
                {getCategoryName(category)}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Grid - With proper spacing */}
        <div className="space-y-16">
          {searchFilteredMenu.map((category, catIndex) => (
            <div key={catIndex} data-testid={`menu-category-${catIndex}`} className="pt-8">
              {/* Category Header */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100">
                  <img 
                    src={categoryImages[category.name] || categoryImages['Italiensk Pizza']}
                    alt={getCategoryName(category)}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-2xl text-gray-900">
                    {getCategoryName(category)}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    {category.items.length} {language === 'no' ? 'retter' : 'items'}
                  </p>
                </div>
              </div>

              {/* Menu Items Grid - 3 columns with proper gap */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.items.map((item, itemIndex) => (
                  <button 
                    key={itemIndex}
                    onClick={() => setSelectedItem({ ...item, category: category.name })}
                    data-testid={`menu-item-${catIndex}-${itemIndex}`}
                    className="menu-card p-6 text-left w-full"
                  >
                    <div className="flex justify-between items-start gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                      <span className="price-tag text-lg flex-shrink-0">
                        {item.sizes ? `fra ${Math.min(...item.sizes.map(s => s.price))}` : item.price} kr
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                    {item.sizes && (
                      <span className="inline-block mt-3 text-xs text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                        {language === 'no' ? 'Flere størrelser' : 'Multiple sizes'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Order CTA */}
        <div className="mt-16 text-center bg-white rounded-2xl p-10 card-shadow">
          <h3 className="font-heading text-2xl text-gray-900 mb-3">
            {language === 'no' ? 'Klar til å bestille?' : 'Ready to order?'}
          </h3>
          <p className="text-gray-500 mb-6">
            {language === 'no' ? 'Bestill online for henting eller levering' : 'Order online for pickup or delivery'}
          </p>
          <a
            href="https://www.foodbooking.com/ordering/restaurant/menu?company_uid=92b70c89-1859-4f9b-864f-0c24c0fbed09&restaurant_uid=2db076f7-d971-4fe8-83fc-571f5efa193e&facebook=true"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="menu-order-btn"
            className="btn-primary inline-block"
          >
            {t('orderNow')}
          </a>
        </div>
      </div>

      {/* Item Dialog */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-gray-900">
              {selectedItem?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-600">{selectedItem?.description}</p>
            
            {selectedItem?.sizes ? (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">
                  {language === 'no' ? 'Velg størrelse' : 'Select size'}
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {selectedItem.sizes.map((size, index) => (
                    <button key={index} className="size-btn flex flex-col items-center py-4">
                      <span className="text-sm font-medium">{size.name}</span>
                      <span className="price-tag text-lg">{size.price} kr</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-xl p-4 flex justify-between items-center">
                <span className="font-medium text-gray-700">{language === 'no' ? 'Pris' : 'Price'}</span>
                <span className="price-tag text-xl">{selectedItem?.price} kr</span>
              </div>
            )}
            
            <a
              href="https://www.foodbooking.com/ordering/restaurant/menu?company_uid=92b70c89-1859-4f9b-864f-0c24c0fbed09&restaurant_uid=2db076f7-d971-4fe8-83fc-571f5efa193e&facebook=true"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full text-center block"
            >
              {t('orderNow')}
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
